import { assert } from "@lib/utils/utils";
import Matrix from "./matrix";
import type Vector from "./vector";

export type NamedMatrixObject = {
  rowNames: string[];
  colNames: string[];
  data: number[];
};

export default class NamedMatrix<R extends string, C extends string> {
  matrix: Matrix<R, C>;
  rowNames: string[];
  colNames: string[];

  constructor(
    data: number[][] | Matrix<R, C>,
    rowNames: string[],
    colNames: string[],
  ) {
    this.matrix = data instanceof Matrix ? data : new Matrix(data);

    // With no evaluator rows, the solver (or an empty row array) cannot infer
    // the proposal count. The supplied column names still define that axis.
    if (this.matrix.n_rows === 0 && this.matrix.n_cols === 0) {
      this.matrix = Matrix.zeros<R, C>(0, colNames.length);
    }

    assert(
      this.matrix.n_rows === rowNames.length,
      () => `Expected ${this.matrix.n_rows} rows, got ${rowNames.length}`,
    );
    assert(
      this.matrix.n_cols === colNames.length,
      () => `Expected ${this.matrix.n_cols} cols, got ${colNames.length}`,
    );
    this.rowNames = rowNames;
    this.colNames = colNames;
  }

  toObject(): NamedMatrixObject {
    return {
      rowNames: this.rowNames,
      colNames: this.colNames,
      data: this.matrix.toObject().data,
    };
  }

  static fromObject<R extends string, C extends string>(
    object: NamedMatrixObject,
  ): NamedMatrix<R, C> {
    return new NamedMatrix(
      Matrix.fromObject<R, C>({
        n_rows: object.rowNames.length,
        n_cols: object.colNames.length,
        data: object.data,
      }),
      object.rowNames,
      object.colNames,
    );
  }

  static fromMatrix<R extends string, C extends string>(
    matrix: Matrix<R, C>,
    rowNames: string[],
    colNames: string[],
  ): NamedMatrix<R, C> {
    // Round-tripping through rows loses the column count for a 0 × N matrix.
    // Keep the explicit dimensions and the existing independent-copy behavior.
    return new NamedMatrix(
      new Matrix<R, C>(matrix.data.slice(), matrix.n_rows, matrix.n_cols),
      rowNames,
      colNames,
    );
  }

  static ofNamedRows<R extends string, C extends string>(
    rows: Vector<C>[],
    rowNames: string[],
    colNames: string[],
  ): NamedMatrix<R, C> {
    return NamedMatrix.fromMatrix(
      Matrix.ofRows<R, C>(rows),
      rowNames,
      colNames,
    );
  }

  getRowName(row: string): Vector<C> {
    return this.matrix.getRow(this.rowNames.indexOf(row));
  }

  getColName(col: string): Vector<R> {
    return this.matrix.getCol(this.colNames.indexOf(col));
  }

  getNames(row: string, col: string): number {
    const rowIndex = this.rowNames.indexOf(row);
    const colIndex = this.colNames.indexOf(col);
    assert(rowIndex !== -1, `Row not found: ${row}`);
    assert(colIndex !== -1, `Column not found: ${col}`);
    return this.matrix.get(rowIndex, colIndex);
  }

  setNames(row: string, col: string, value: number): void {
    this.matrix.set(
      this.rowNames.indexOf(row),
      this.colNames.indexOf(col),
      value,
    );
  }

  rename<NewR extends string, NewC extends string>(
    _rowRename: `${R} -> ${NewR}`,
    _colRename: `${C} -> ${NewC}`,
  ): NamedMatrix<NewR, NewC> {
    return new NamedMatrix(
      this.matrix.rename(_rowRename, _colRename),
      this.rowNames,
      this.colNames,
    );
  }

  map(fn: (value: number) => number): NamedMatrix<R, C> {
    return new NamedMatrix(
      this.matrix.toArrays().map((row) => row.map(fn)),
      this.rowNames,
      this.colNames,
    );
  }

  corner(n: number = 5): number[][] {
    return this.matrix
      .toArrays()
      .slice(0, n)
      .map((row) => row.slice(0, n));
  }

  toArrays(): number[][] {
    return this.matrix.toArrays();
  }

  toNamedArrays(): (number | string)[][] {
    const result: (number | string)[][] = [];
    result.push([""].concat(this.colNames));

    for (let i = 0; i < this.rowNames.length; i++) {
      const rowName = this.rowNames[i];

      if (rowName == null) {
        throw new Error(`Missing row name at index ${i}`);
      }

      result.push(
        ([rowName] as (number | string)[]).concat(
          this.getRowName(rowName).toArray() as (number | string)[],
        ),
      );
    }

    return result;
  }

  get transpose(): NamedMatrix<C, R> {
    return new NamedMatrix(this.matrix.transpose, this.colNames, this.rowNames);
  }

  mergeRows<NewR extends string>(mapping: {
    [oldRow: string]: string;
  }): NamedMatrix<NewR, C> {
    const newRowNames: string[] = [];
    const newRows: Vector<C>[] = [];

    for (const [oldRow, newRow] of Object.entries(mapping)) {
      const maybeIndex = newRowNames.indexOf(newRow);
      const index = maybeIndex === -1 ? newRowNames.length : maybeIndex;

      if (maybeIndex === -1) {
        newRowNames.push(newRow);
        newRows.push(this.getRowName(oldRow));
      } else {
        const existingRow = newRows[index];

        if (existingRow == null) {
          throw new Error(`Missing merged row at index ${index}`);
        }

        newRows[index] = existingRow.add(this.getRowName(oldRow));
      }
    }

    return new NamedMatrix(Matrix.ofRows(newRows), newRowNames, this.colNames);
  }

  mergeCols<NewC extends string>(mapping: {
    [oldCol: string]: string;
  }): NamedMatrix<R, NewC> {
    const x: NamedMatrix<C, R> = this.transpose;
    const y: NamedMatrix<NewC, R> = x.mergeRows(mapping);
    const z: NamedMatrix<R, NewC> = y.transpose;
    return z;
  }

  filterRows(filter: (row: string) => boolean): NamedMatrix<R, C> {
    return new NamedMatrix(
      this.matrix.toArrays().filter((_row, index) => {
        const rowName = this.rowNames[index];
        return rowName != null && filter(rowName);
      }),
      this.rowNames.filter((rowName) => rowName != null && filter(rowName)),
      this.colNames,
    );
  }

  filterCols(filter: (col: string) => boolean): NamedMatrix<R, C> {
    return new NamedMatrix(
      this.matrix.toArrays().map((row) =>
        row.filter((_col, index) => {
          const colName = this.colNames[index];
          return colName != null && filter(colName);
        }),
      ),
      this.rowNames,
      this.colNames.filter((colName) => colName != null && filter(colName)),
    );
  }

  sum<Axis extends 0 | 1>(axis: Axis): Vector<Axis extends 0 ? C : R> {
    return this.matrix.sum(axis);
  }

  add(other: NamedMatrix<R, C>): NamedMatrix<R, C> {
    assert(
      this.rowNames.length === other.rowNames.length &&
        this.rowNames.every((name, i) => name === other.rowNames[i]),
      () =>
        `NamedMatrix.add: row names must match. this: [${this.rowNames.join(", ")}], other: [${other.rowNames.join(", ")}]`,
    );
    assert(
      this.colNames.length === other.colNames.length &&
        this.colNames.every((name, i) => name === other.colNames[i]),
      () =>
        `NamedMatrix.add: column names must match. this: [${this.colNames.join(", ")}], other: [${other.colNames.join(", ")}]`,
    );
    return new NamedMatrix(
      this.matrix.add(other.matrix),
      this.rowNames,
      this.colNames,
    );
  }

  sub(other: NamedMatrix<R, C>): NamedMatrix<R, C> {
    assert(
      this.rowNames.length === other.rowNames.length &&
        this.rowNames.every((name, i) => name === other.rowNames[i]),
      () =>
        `NamedMatrix.sub: row names must match. this: [${this.rowNames.join(", ")}], other: [${other.rowNames.join(", ")}]`,
    );
    assert(
      this.colNames.length === other.colNames.length &&
        this.colNames.every((name, i) => name === other.colNames[i]),
      () =>
        `NamedMatrix.sub: column names must match. this: [${this.colNames.join(", ")}], other: [${other.colNames.join(", ")}]`,
    );
    return new NamedMatrix(
      this.matrix.sub(other.matrix),
      this.rowNames,
      this.colNames,
    );
  }

  max(): number {
    return this.matrix.max();
  }

  min(): number {
    return this.matrix.min();
  }
}
